import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Store } from '@ngxs/store';
import { lastValueFrom, timeout } from 'rxjs';
import { API_CONFIGURATION } from '@lexplus-environments';
import { ProfileState, ProfileStateSetKundeDokumentenManagerAktiv } from '@lexplus-shared-lib';
import { kundeModulesControllerGetKundeModulesV1 } from '@lexplus-user-api';

/**
 * Keeps the profile's module switches on the effective Kunde, as `GET /v1/kunden/modules`
 * reports them — today only the Dokumenten-Manager's.
 *
 * The profile is fetched at sign-in only and describes the user's own Kunde, while a Neosys admin
 * works in the Kunde they impersonate. Refreshed on every page load; starting or stopping an
 * impersonation reloads the page, so the switches follow it.
 */
@Service()
export class KundeModulesService {
  private readonly store = inject(Store);
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly requestTimeoutMs = 5000;

  /**
   * Stores the effective Kunde's module switches in the profile.
   *
   * Does nothing while nobody is signed in, and keeps the stored switches when the request fails or
   * times out, so a slow API delays the first page by at most `requestTimeoutMs`.
   */
  public async refresh(): Promise<void> {
    if (!this.store.selectSnapshot(ProfileState.isLoggedIn)) {
      return;
    }

    try {
      const response = await lastValueFrom(
        kundeModulesControllerGetKundeModulesV1(this.httpClient, this.apiConfiguration.baseUrlUser).pipe(timeout(this.requestTimeoutMs)),
      );
      this.store.dispatch(new ProfileStateSetKundeDokumentenManagerAktiv(response.body.dokumentenManagerAktiv));
    } catch {
      // The stored switch keeps deciding until the next page load.
    }
  }
}
